import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractError, BACKEND_URL } from "@/lib/api";
import { SITTER, NAV } from "@/constants/testIds";
import { toast } from "sonner";
import {
  BadgeCheck, Pencil, DollarSign, MapPin, Star,
  CalendarClock, MessagesSquare, Search, Camera, AlertTriangle
} from "lucide-react";
import FileUpload from "@/components/FileUpload";
import StripeConnectCard from "@/components/StripeConnectCard";
import SitterVerificationSection from "@/components/SitterVerificationSection";
import SitterProfileCompletion from "@/components/SitterProfileCompletion";

export default function SitterDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);

  const load = async () => {
    try {
      const [p, b] = await Promise.all([api.get("/sitters/me"), api.get("/bookings")]);
      setProfile(p.data);
      setBookings(b.data.results);
    } catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const onPhotoUploaded = async (upload) => {
    try {
      const photoUrl = `${BACKEND_URL}/api/uploads/${upload.path}`;
      await api.put("/sitters/me", { ...profile, photo_url: photoUrl });
      toast.success("Photo uploaded");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  if (!profile) return (<div className="min-h-screen bg-stone2-50"><Navbar /><div className="p-10 text-stone2-500">Loading…</div></div>);

  const active = bookings.filter((b) => ["requested", "accepted", "paid"].includes(b.status));
  const hasPhoto = !!(profile.photo_url || profile.photo_path);

  return (
    <div className="min-h-screen bg-stone2-50" data-testid={SITTER.dashboard}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="section-eyebrow">Sitter dashboard</span>
            <h1 className="mt-2 text-3xl lg:text-4xl font-heading font-bold text-sage-900 flex items-center gap-3">
              Hi, {user?.name?.split(" ")[0]}
              {profile.verified && <span className="chip"><BadgeCheck className="w-3.5 h-3.5 mr-1" /> Verified</span>}
            </h1>
            <p className="text-stone2-600 mt-1">Keep your profile complete to appear in more family searches.</p>
          </div>
          <Link to="/sitter/profile" className="btn-primary" data-testid={SITTER.editProfile}>
            <Pencil className="w-4 h-4" /> Edit profile
          </Link>
        </div>

        <SitterProfileCompletion profile={profile} />

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          <section className="paper-card p-6 lg:col-span-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {profile.photo_url ? (
                  <img src={profile.photo_url} alt={profile.name}
                    data-testid="sitter-photo"
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-sage-100" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-sage-600 text-white flex items-center justify-center font-heading font-bold text-lg">
                    {profile.name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <div>
                  <div className="font-heading font-semibold text-sage-900 text-lg">{profile.name}</div>
                  <div className="text-sm text-stone2-500 flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {profile.city || "—"}, {profile.state || "—"}</span>
                    <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> ${profile.hourly_rate}/hr</span>
                    <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-ochre-500 fill-ochre-500" /> {profile.rating_avg || "—"} ({profile.rating_count || 0})</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Photo required banner */}
            {!hasPhoto && (
              <div className="mt-4 rounded-xl border-2 border-dashed border-ochre-300 bg-ochre-50 p-4 flex items-start justify-between gap-3 flex-wrap"
                data-testid="sitter-photo-required-banner">
                <div className="flex items-start gap-3 min-w-0">
                  <AlertTriangle className="w-5 h-5 text-ochre-700 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-heading font-semibold text-sage-900 flex items-center gap-1.5">
                      <Camera className="w-4 h-4" /> Photo required
                    </div>
                    <p className="mt-0.5 text-xs text-stone2-700 leading-relaxed">
                      Sitters need a professional photo (headshot preferred) before appearing in family searches or earning the Verified badge.
                      Families trust caregivers with a face.
                    </p>
                  </div>
                </div>
                <FileUpload testId="sitter-photo-upload" label="Upload photo" onUploaded={onPhotoUploaded} />
              </div>
            )}

            <p className="mt-4 text-sm text-stone2-700 leading-relaxed">{profile.biography || "No bio yet — add one to help families connect."}</p>

            <div className="mt-5 flex flex-wrap gap-1">
              {(profile.certifications || []).map((c) => <span key={c} className="chip" data-testid={`sitter-cert-${c}`}>{c}</span>)}
              {profile.aba_trained && <span className="chip-ochre">ABA trained</span>}
              {profile.overnight_care && <span className="chip-ochre">Overnight OK</span>}
              {profile.can_transport && <span className="chip-ochre">Can transport</span>}
            </div>
          </section>

          <aside className="paper-card p-6 flex flex-col gap-4">
            <div>
              <span className="section-eyebrow">Quick actions</span>
              <h2 className="mt-2 font-heading font-semibold text-sage-900 text-lg">Manage your work</h2>
            </div>
            <Link to="/listings" className="btn-primary justify-center" data-testid="sitter-browse-listings-btn">
              <Search className="w-4 h-4" /> Browse family listings
            </Link>
            <Link to="/bookings" className="btn-outline-sage justify-center" data-testid="sitter-bookings-btn">
              <CalendarClock className="w-4 h-4" /> Bookings ({active.length})
            </Link>
            <Link to="/messages" className="btn-secondary justify-center" data-testid="sitter-messages-btn">
              <MessagesSquare className="w-4 h-4" /> Messages
            </Link>
            <Link to="/sitter/invoices" className="btn-outline-sage justify-center" data-testid="sitter-invoices-btn">
              <DollarSign className="w-4 h-4" /> Send an invoice
            </Link>
            <div className="rounded-lg bg-sage-50 p-4">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-sage-700">Active bookings</div>
              <div className="text-3xl font-heading font-bold text-sage-900 mt-1" data-testid="sitter-active-bookings-count">{active.length}</div>
            </div>
          </aside>
        </div>

        {/* Verification (new per-item component) */}
        <SitterVerificationSection profile={profile} onChanged={load} />

        {/* Stripe Connect */}
        <div className="mt-8">
          <StripeConnectCard />
        </div>
      </div>
    </div>
  );
}
